import { Injectable } from '@angular/core';
import { NavigationEnd } from '@angular/router';
import {
  ElioTypedRouter,
  PathRouter,
  START_URL_ABSOLUTE_PATH,
  StaticPathRouter,
} from '@elio/emanager-front/shared/tools/router';
import { ComponentStore } from '@ngrx/component-store';
import { concatLatestFrom } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { filter, first, iif, map, mergeMap, Observable, of, pairwise, tap } from 'rxjs';
import { RouterSelectors } from './router.selectors';

interface RouterUrlState {
  previousRouteUrl: PathRouter[];
}

const DEFAULT_PREVIOUS_URL = [START_URL_ABSOLUTE_PATH];

@Injectable({ providedIn: 'root' })
export class RouterUrlService extends ComponentStore<RouterUrlState> {
  readonly previousRouteUrl$: Observable<PathRouter[]> = this.select((state) => state.previousRouteUrl);

  constructor(
    private router: ElioTypedRouter,
    private store: Store
  ) {
    super({
      previousRouteUrl: DEFAULT_PREVIOUS_URL,
    });
  }

  readonly navigateBackToPreviousPage = this.effect((defaultPreviousUrl$: Observable<StaticPathRouter[]>) => {
    return defaultPreviousUrl$.pipe(
      concatLatestFrom((defaultPreviousUrl) => [
        this.previousRouteUrl$,
        this.store.select(RouterSelectors.getUrlWithReplacedParams(defaultPreviousUrl)),
        this.store.select(RouterSelectors.getRouteUrl),
      ]),
      mergeMap(([, previousRouteUrl, defaultUrlWithParams, currentUrl]) =>
        iif(
          () => defaultUrlWithParams.join('/') !== `/${currentUrl}` && previousRouteUrl !== DEFAULT_PREVIOUS_URL,
          of(previousRouteUrl),
          of(defaultUrlWithParams)
        ).pipe(first())
      ),
      tap((routeToNavigate) => this.router.navigate([...routeToNavigate]))
    );
  });

  private updatePreviousUrl = this.updater(
    (state, previousUrl: string): RouterUrlState => ({
      ...state,
      previousRouteUrl: previousUrl.split('/') as PathRouter[],
    })
  );

  listenRoutes() {
    this.updatePreviousUrl(
      this.router.events.pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        pairwise(),
        map((events: NavigationEnd[]) => events[0].urlAfterRedirects)
      )
    );
  }
}
